import { useState, useEffect, useRef, useCallback } from "react";
import { Tooltip } from "antd";
import moment from "moment";
import { BRAND, UNLIMITED, isUnlimited } from "./planShared";

/**
 * Pieces the Smart Search customer screens share.
 *
 * The list and the three detail tabs all draw the same chips, the same skeleton
 * and the same empty state; keeping them here is what stops four near-copies
 * from drifting apart.
 */

export { BRAND, UNLIMITED, isUnlimited };

export const INK = { strong: "#0f172a", body: "#475569", muted: "#94a3b8", faint: "#cbd5e1" };

export const TONE = {
  green: { color: "#059669", bg: "#ecfdf5", border: "#a7f3d0" },
  amber: { color: "#b45309", bg: "#fffbeb", border: "#fde68a" },
  red: { color: "#dc2626", bg: "#fef2f2", border: "#fecaca" },
  violet: { color: "#6d28d9", bg: "#f5f3ff", border: "#ddd6fe" },
  indigo: { color: "#4f46e5", bg: "#eef2ff", border: "#c7d2fe" },
  teal: { color: "#0d9488", bg: "#f0fdfa", border: "#99f6e4" },
  slate: { color: "#64748b", bg: "#f1f5f9", border: "#e2e8f0" },
};

export const chip = (text, tone = TONE.slate, extra = {}) => (
  <span style={{
    display: "inline-flex", alignItems: "center", padding: "2px 9px", borderRadius: 20,
    fontSize: 10.5, fontWeight: 700, letterSpacing: "0.3px", textTransform: "uppercase",
    color: tone.color, background: tone.bg, border: `1px solid ${tone.border}`,
    whiteSpace: "nowrap", ...extra,
  }}>
    {text}
  </span>
);

export const titleCase = (v) => String(v || "")
  .replace(/[_-]+/g, " ")
  .replace(/\b\w/g, (c) => c.toUpperCase())
  .trim();

/**
 * A subscription status, coloured by what it means.
 *
 * Only these three carry a colour on purpose — green is running, amber is the
 * customer's own decision to stop, red is it having run out. A status nobody
 * here has heard of gets neutral slate and its own name, which reads as
 * "something else" rather than as a mistake.
 */
const STATUS_TONE = {
  active: TONE.green,
  trialing: TONE.indigo,
  canceled: TONE.amber,
  cancelled: TONE.amber,
  expired: TONE.red,
  past_due: TONE.red,
};

export const statusChip = (value) => (value
  ? chip(titleCase(value), STATUS_TONE[String(value).toLowerCase()] || TONE.slate)
  : <span style={{ color: INK.faint }}>—</span>);

// ── Numbers, dates, quota ────────────────────────────────────────────────────
export const num = (v) => (v === null || v === undefined || v === ""
  ? "—"
  : Number(v).toLocaleString("en-IN"));

export const inr = (v) => {
  if (v === null || v === undefined || v === "") return "—";
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return `₹${n.toLocaleString("en-IN", {
    minimumFractionDigits: Math.abs(n % 1) > 0 ? 2 : 0,
    maximumFractionDigits: 2,
  })}`;
};

export const dateOnly = (v) => {
  if (!v) return "—";
  const m = moment(v);
  return m.isValid() ? m.format("DD MMM YYYY") : String(v);
};

export const dateTime = (v) => {
  if (!v) return "—";
  const m = moment(v);
  return m.isValid() ? m.format("DD MMM YYYY, HH:mm") : String(v);
};

export const relative = (v) => {
  if (!v) return "—";
  const m = moment(v);
  return m.isValid() ? m.fromNow() : String(v);
};

/**
 * What a quota adds up to.
 *
 * `percent` is null when there is no ceiling — a percentage of unlimited is not
 * 100%, it is not a number at all, and a bar filled to the end would say the
 * customer had run out.
 */
export const quotaOf = (used, quota) => {
  const u = Number(used || 0);
  const unlimited = isUnlimited(quota);
  const q = unlimited ? null : Number(quota || 0);
  return {
    used: u,
    quota: q,
    unlimited,
    remaining: unlimited ? null : Math.max(0, (q || 0) - u),
    percent: unlimited || !q ? null : Math.min(100, Math.round((u / q) * 100)),
    label: unlimited ? `${num(u)} / Unlimited` : `${num(u)} / ${num(q)}`,
  };
};

/* Colour by how close to the ceiling, because that is the only thing anyone
   reads a quota bar for. */
const quotaColor = (pct) => {
  if (pct === null) return BRAND;
  if (pct >= 90) return TONE.red.color;
  if (pct >= 70) return TONE.amber.color;
  return BRAND;
};

/** A slim quota bar for a table row. */
export const QuotaBar = ({ used, quota, width = 118 }) => {
  const q = quotaOf(used, quota);
  const color = quotaColor(q.percent);
  return (
    <div style={{ width }}>
      <div style={{
        fontSize: 12, color: INK.body, fontWeight: 600, marginBottom: 5,
        fontVariantNumeric: "tabular-nums",
      }}>
        {q.label}
      </div>
      <div style={{ height: 5, borderRadius: 99, background: "#f1f5f9", overflow: "hidden" }}>
        <div style={{
          /* Unlimited gets a thin constant sliver rather than a full bar: it
             has to read as "not measured against anything", not as "full". */
          width: q.percent === null ? "22%" : `${Math.max(2, q.percent)}%`,
          height: "100%", borderRadius: 99, background: color,
          opacity: q.percent === null ? 0.35 : 1,
          transition: "width .5s ease",
        }} />
      </div>
    </div>
  );
};

/** The same figure, as a ring, for the detail page. */
export const QuotaRing = ({ used, quota, size = 132 }) => {
  const q = quotaOf(used, quota);
  const color = quotaColor(q.percent);
  const r = (size - 14) / 2;
  const c = 2 * Math.PI * r;
  const filled = q.percent === null ? c : (q.percent / 100) * c;

  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
        <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="#f1f5f9" strokeWidth="11" />
        <circle
          cx={size / 2} cy={size / 2} r={r} fill="none"
          stroke={color} strokeWidth="11" strokeLinecap="round"
          strokeDasharray={`${filled} ${c}`}
          opacity={q.percent === null ? 0.22 : 1}
          style={{ transition: "stroke-dasharray .6s ease" }}
        />
      </svg>
      <div style={{
        position: "absolute", inset: 0, display: "flex", flexDirection: "column",
        alignItems: "center", justifyContent: "center",
      }}>
        {q.unlimited ? (
          <>
            <div style={{ fontSize: 15, fontWeight: 800, color: INK.strong }}>Unlimited</div>
            <div style={{ fontSize: 11, color: INK.muted, marginTop: 2 }}>{num(q.used)} used</div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 24, fontWeight: 800, color: INK.strong, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
              {q.percent}%
            </div>
            <div style={{ fontSize: 11, color: INK.muted, marginTop: 4, fontVariantNumeric: "tabular-nums" }}>
              {num(q.used)} / {num(q.quota)}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

// ── Frames ───────────────────────────────────────────────────────────────────
export const cardStyle = {
  background: "#fff", borderRadius: 16, border: "1px solid #eceef5",
  boxShadow: "0 1px 3px rgba(15,23,42,0.04), 0 10px 26px rgba(15,23,42,0.04)",
};

export const StatTile = ({ icon, label, value, sub, color = BRAND }) => (
  <div style={{
    flex: 1, minWidth: 158, background: "#fff", border: "1px solid #eceef5",
    borderRadius: 14, padding: "15px 16px",
    boxShadow: "0 1px 3px rgba(15,23,42,0.03)",
  }}>
    <div style={{ display: "flex", alignItems: "center", gap: 9, marginBottom: 10 }}>
      <span style={{
        width: 28, height: 28, borderRadius: 9, display: "inline-flex",
        alignItems: "center", justifyContent: "center",
        background: `${color}14`, color, fontSize: 13,
      }}>
        {icon}
      </span>
      <span style={{ fontSize: 11, color: INK.body, fontWeight: 600 }}>{label}</span>
    </div>
    <div style={{ fontSize: 21, fontWeight: 700, color: INK.strong, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>
      {value}
    </div>
    {sub && <div style={{ fontSize: 11, color: INK.muted, marginTop: 6 }}>{sub}</div>}
  </div>
);

export const Skeleton = ({ rows = 6, height = 44 }) => (
  <div style={{ padding: 4 }}>
    {Array.from({ length: rows }).map((_, i) => (
      <div key={i} style={{
        height, borderRadius: 10, background: "#f4f6fa", marginBottom: 8,
        animation: "ssPulse 1.3s ease-in-out infinite",
        animationDelay: `${i * 0.06}s`,
      }} />
    ))}
  </div>
);

export const PanelEmpty = ({ message = "Nothing here yet." }) => (
  <div style={{
    padding: "46px 20px", textAlign: "center", color: INK.muted, fontSize: 13,
    background: "#fafbff", border: "1px dashed #e5e9f0", borderRadius: 12,
  }}>
    {message}
  </div>
);

export const PanelError = ({ message, onRetry }) => (
  <div style={{
    padding: "34px 20px", textAlign: "center", background: "#fff8f6",
    border: "1px dashed #f3c6b5", borderRadius: 12, color: "#9a3412", fontSize: 13,
  }}>
    {message}
    {onRetry && (
      <div style={{ marginTop: 12 }}>
        <button
          onClick={onRetry}
          style={{
            background: "#fff", border: "1px solid #f3c6b5", color: "#9a3412",
            borderRadius: 8, padding: "5px 14px", fontSize: 12.5, fontWeight: 600, cursor: "pointer",
          }}
        >
          Try again
        </button>
      </div>
    )}
  </div>
);

/** A label/value pair, for the detail page's information cards. */
export const Row = ({ label, children, tip }) => (
  <div style={{
    display: "flex", alignItems: "baseline", gap: 12, padding: "8px 0",
    borderBottom: "1px solid #f6f8fc",
  }}>
    <span style={{ fontSize: 11.5, color: INK.muted, minWidth: 128, flexShrink: 0 }}>
      {tip ? <Tooltip title={tip}><span style={{ borderBottom: "1px dotted #cbd5e1" }}>{label}</span></Tooltip> : label}
    </span>
    <span style={{ fontSize: 12.5, color: INK.strong, fontWeight: 600, textAlign: "right", marginLeft: "auto" }}>
      {children}
    </span>
  </div>
);

/**
 * Data for one tab, fetched the first time that tab is opened.
 *
 * Three things it has to get right: nothing is requested until `active`, the
 * answer is kept when the reader switches away and back, and a response that
 * arrives after the reader has already moved on is dropped. Without that last
 * one, typing in a search box shows whichever request happened to finish last
 * rather than the one matching what is on screen.
 */
export const useLazyList = (fetcher, { active, deps = [], perPage = 15 } = {}) => {
  const [state, setState] = useState({ rows: [], total: 0, loading: false, error: "", loaded: false });
  const [page, setPage] = useState(1);
  const wanted = useRef(0);

  const key = JSON.stringify(deps);

  // A new filter is a new list; staying on page 4 of the old one shows nothing.
  useEffect(() => { setPage(1); }, [key]);

  const load = useCallback(async () => {
    const ticket = ++wanted.current;
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const { rows, total } = await fetcher(page, perPage);
      if (ticket !== wanted.current) return;
      setState({ rows, total, loading: false, error: "", loaded: true });
    } catch (err) {
      if (ticket !== wanted.current) return;
      setState({ rows: [], total: 0, loading: false, error: err.message, loaded: true });
    }
    // `fetcher` is rebuilt every render by its caller; `key` is what actually
    // changes the request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, perPage, key]);

  useEffect(() => {
    if (!active) return;
    load();
  }, [active, load]);

  return {
    ...state, page, perPage, setPage, reload: load,
    pageCount: Math.max(1, Math.ceil(state.total / perPage)),
  };
};

/** Types fast, asks slowly. */
export const useDebounced = (value, delay = 400) => {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return v;
};

export const SS_CSS = `
  @keyframes ssPulse { 0%,100%{opacity:1} 50%{opacity:.5} }
  @keyframes ssFadeUp { from{opacity:0;transform:translateY(8px)} to{opacity:1;transform:translateY(0)} }
  .ss-card { animation: ssFadeUp .3s ease both; }
  .ss-row:hover td { background: #fafbff; }
  .ss-sc::-webkit-scrollbar{width:5px;height:5px}
  .ss-sc::-webkit-scrollbar-thumb{background:#e2e8f0;border-radius:99px}
`;

/**
 * A list that grows as the reader reaches the end of it.
 *
 * Three things keep it honest. One request at a time, held by a ref rather than
 * state so a second trigger in the same tick cannot slip past a value that has
 * not re-rendered yet. A ticket per run, so a response for filters the reader
 * has already changed is dropped instead of appended to a list it does not
 * belong to. And paging driven by the server's own `current_page < last_page`,
 * so "is there more" is never inferred from how many rows came back — a final
 * page that happens to be full would otherwise look like there is another one.
 */
export const useInfiniteList = (fetcher, { deps = [], perPage = 20 } = {}) => {
  const [state, setState] = useState({
    rows: [], total: 0, page: 0, lastPage: 1, loading: false, error: "", loaded: false,
  });
  const inFlight = useRef(false);
  const wanted = useRef(0);
  const key = JSON.stringify(deps);

  const loadPage = useCallback(async (page, replace) => {
    if (inFlight.current) return;
    inFlight.current = true;
    const ticket = wanted.current;
    setState((s) => ({ ...s, loading: true, error: "" }));
    try {
      const r = await fetcher(page, perPage);
      if (ticket !== wanted.current) return;
      setState((s) => ({
        rows: replace ? r.rows : [...s.rows, ...r.rows],
        total: r.total,
        page: r.currentPage || page,
        lastPage: r.lastPage || 1,
        loading: false,
        error: "",
        loaded: true,
      }));
    } catch (err) {
      if (ticket !== wanted.current) return;
      /* Rows already on screen stay. Losing a hundred loaded rows because the
         next page failed is a worse answer than showing the error under them. */
      setState((s) => ({ ...s, loading: false, error: err.message, loaded: true }));
    } finally {
      inFlight.current = false;
    }
    // `fetcher` is rebuilt each render by the caller; `key` is what changes the
    // request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [perPage, key]);

  /* A filter change is a different list, so the ticket moves and everything
     already on screen goes with it. */
  useEffect(() => {
    wanted.current += 1;
    inFlight.current = false;
    setState({ rows: [], total: 0, page: 0, lastPage: 1, loading: false, error: "", loaded: false });
    loadPage(1, true);
  }, [loadPage]);

  const hasMore = state.loaded && state.page > 0 && state.page < state.lastPage;

  const loadMore = useCallback(() => {
    if (!hasMore || inFlight.current || state.error) return;
    loadPage(state.page + 1, false);
  }, [hasMore, state.page, state.error, loadPage]);

  const reload = useCallback(() => {
    wanted.current += 1;
    inFlight.current = false;
    setState({ rows: [], total: 0, page: 0, lastPage: 1, loading: false, error: "", loaded: false });
    loadPage(1, true);
  }, [loadPage]);

  return { ...state, hasMore, loadMore, reload };
};

/**
 * Calls `onHit` when the element this returns a ref for comes into view.
 *
 * An observer rather than a throttled scroll listener: it fires when the
 * sentinel is actually visible instead of on every scroll event, so there is
 * nothing to throttle and nothing to recompute. `rootMargin` starts the next
 * page while the end is still a screen away, so the list usually grows before
 * the reader reaches the bottom.
 */
export const useEndSentinel = (onHit, enabled) => {
  const ref = useRef(null);
  const handler = useRef(onHit);
  handler.current = onHit;

  useEffect(() => {
    const node = ref.current;
    if (!node || !enabled) return undefined;
    const io = new IntersectionObserver(
      (entries) => { if (entries[0]?.isIntersecting) handler.current(); },
      { rootMargin: "400px 0px" },
    );
    io.observe(node);
    return () => io.disconnect();
  }, [enabled]);

  return ref;
};

/**
 * What this customer was actually charged.
 *
 * `payment` is the real transaction and it is what an upgrade rewrites, so it
 * is the truth about money. `total_amount_inr` on the row is the subscription's
 * own snapshot — right until someone upgrades, then stale. The snapshot is kept
 * only as a fallback for a row that has no payment attached yet.
 *
 * Every figure is the server's. Nothing here adds base and tax together, or
 * derives one from the other.
 */
export const paymentOf = (row) => {
  const p = row?.payment;
  const live = p && typeof p === "object" && p.amount_inr !== null && p.amount_inr !== undefined;
  return {
    total: live ? p.amount_inr : row?.total_amount_inr,
    base: live ? p.base_inr : row?.monthly_price_inr,
    tax: live ? p.tax_inr : row?.tax_amount_inr,
    percent: live ? p.tax_percent : row?.tax_percent,
    /* False means the amount shown is the subscription snapshot, not a charge —
       worth saying where there is room for it. */
    charged: Boolean(live),
  };
};

/** Whether a tax figure is worth printing at all. */
export const hasTax = (v) => v !== null && v !== undefined && v !== "" && Number(v) > 0;
